import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ImageUploader } from "@/pages/Publish";
import { ArrowLeft, Plus, X } from "lucide-react";
import { L } from "@/pages/marketplace/Sell";

const inputCls = "w-full px-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm outline-none focus:border-black";

export default function ProductForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = id && id !== "novo";
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: "", description: "", category: "", photos: [], price: "", currency: "GBP", stock: 1, sku: "", variants: [],
    fulfilment: { modes: ["pickup"], delivery: { price: "", area: "", conditions: "" }, pickup: { location: "", instructions: "", hours: "" } },
  });
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const setFul = (k, v) => setF(x => ({ ...x, fulfilment: { ...x.fulfilment, [k]: v } }));

  useEffect(() => {
    api.get("/marketplace/categories", { params: { kind: "products" } }).then(r => setCats(r.data));
    if (editing) api.get("/products/mine").then(r => {
      const p = r.data.find(x => x.id === id);
      if (!p) return nav("/vender");
      setF({ ...p, price: (p.price_minor / 100).toFixed(2), sku: p.sku || "",
        fulfilment: { modes: p.fulfilment.modes, delivery: { ...p.fulfilment.delivery, price: (p.fulfilment.delivery.price_minor / 100).toFixed(2) }, pickup: { ...p.fulfilment.pickup } } });
    });
  }, [editing, id, nav]);

  const toggleMode = (m) => {
    const modes = f.fulfilment.modes.includes(m) ? f.fulfilment.modes.filter(x => x !== m) : [...f.fulfilment.modes, m];
    setFul("modes", modes);
  };

  const save = async (e, thenSubmit = false) => {
    e.preventDefault();
    if (f.fulfilment.modes.length === 0) return toast.error("Escolhe pelo menos um método de entrega");
    setBusy(true);
    try {
      const body = {
        name: f.name, description: f.description, category: f.category, photos: f.photos, currency: f.currency,
        price_minor: Math.round(parseFloat(f.price || "0") * 100), stock: parseInt(f.stock || 0, 10), sku: f.sku || null,
        variants: f.variants.filter(v => v.name && v.options.length),
        fulfilment: { modes: f.fulfilment.modes,
          delivery: { price_minor: Math.round(parseFloat(f.fulfilment.delivery.price || "0") * 100), area: f.fulfilment.delivery.area || null, conditions: f.fulfilment.delivery.conditions || null },
          pickup: { location: f.fulfilment.pickup.location || null, instructions: f.fulfilment.pickup.instructions || null, hours: f.fulfilment.pickup.hours || null } },
      };
      const { data } = editing ? await api.patch(`/products/${id}`, body) : await api.post("/products", body);
      if (thenSubmit) {
        try { await api.post(`/products/${data.id}/submit`); toast.success("Produto enviado para revisão"); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      } else toast.success(editing ? "Produto actualizado" : "Rascunho guardado");
      nav("/vender");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fd = f.fulfilment.delivery, fp = f.fulfilment.pickup;
  return (
    <div className="page fade-up">
      <Link to="/vender" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3"><ArrowLeft size={14} /> Minha loja</Link>
      <h1 className="font-display text-2xl font-extrabold mb-3">{editing ? "Editar produto" : "Novo produto"}</h1>
      <form onSubmit={(e) => save(e, false)} className="bg-white rounded-2xl border border-zinc-200 p-4 space-y-3 mb-8">
        <L label="Nome *"><input data-testid="prod-name" required className={inputCls} value={f.name} onChange={e => set("name", e.target.value)} /></L>
        <L label="Descrição *"><textarea data-testid="prod-description" required rows={4} className={inputCls} value={f.description} onChange={e => set("description", e.target.value)} /></L>
        <L label="Categoria *">
          <select data-testid="prod-category" required className={inputCls} value={f.category} onChange={e => set("category", e.target.value)}>
            <option value="">—</option>{cats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </L>
        <L label="Fotografias">
          <div className="space-y-2">
            {f.photos.map((ph, i) => (
              <div key={i} className="relative rounded-lg overflow-hidden border border-zinc-200"><img src={ph} alt="" className="w-full aspect-video object-cover" />
                <button type="button" onClick={() => set("photos", f.photos.filter((_, j) => j !== i))} className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1.5"><X size={14} /></button></div>
            ))}
            {f.photos.length < 6 && <ImageUploader testid={`prod-photo-${f.photos.length}`} value="" onChange={v => v && set("photos", [...f.photos, v])} />}
          </div>
        </L>
        <div className="grid grid-cols-3 gap-2">
          <L label="Preço *"><input data-testid="prod-price" required type="number" step="0.01" min="0" className={inputCls} value={f.price} onChange={e => set("price", e.target.value)} /></L>
          <L label="Moeda"><select className={inputCls} value={f.currency} onChange={e => set("currency", e.target.value)}>{["GBP", "EUR", "USD", "AOA"].map(c => <option key={c}>{c}</option>)}</select></L>
          <L label="Stock *"><input data-testid="prod-stock" required type="number" min="0" className={inputCls} value={f.stock} onChange={e => set("stock", e.target.value)} /></L>
        </div>
        <L label="SKU (opcional)"><input className={inputCls} value={f.sku} onChange={e => set("sku", e.target.value)} /></L>

        <L label="Variantes (opcional)">
          {f.variants.map((v, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input className={inputCls} placeholder="Ex: Tamanho" value={v.name} onChange={e => set("variants", f.variants.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
              <input className={inputCls} placeholder="S, M, L" value={v.options.join(", ")} onChange={e => set("variants", f.variants.map((x, j) => j === i ? { ...x, options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) } : x))} />
              <button type="button" onClick={() => set("variants", f.variants.filter((_, j) => j !== i))} className="text-zinc-400"><X size={16} /></button>
            </div>
          ))}
          <button type="button" onClick={() => set("variants", [...f.variants, { name: "", options: [] }])} className="text-xs font-semibold text-[#c8102e] flex items-center gap-1"><Plus size={12} /> Adicionar variante</button>
        </L>

        <L label="Entrega / Levantamento *">
          <div className="flex gap-1.5 mb-2">
            <button type="button" data-testid="prod-mode-delivery" className="chip !text-[11px]" data-active={f.fulfilment.modes.includes("delivery")} onClick={() => toggleMode("delivery")}>Entrega</button>
            <button type="button" data-testid="prod-mode-pickup" className="chip !text-[11px]" data-active={f.fulfilment.modes.includes("pickup")} onClick={() => toggleMode("pickup")}>Levantamento</button>
          </div>
          {f.fulfilment.modes.includes("delivery") && (
            <div className="grid grid-cols-2 gap-2 p-3 rounded-xl bg-zinc-50 border border-zinc-200 mb-2">
              <input data-testid="prod-delivery-price" type="number" step="0.01" min="0" className={inputCls} placeholder="Preço entrega (0 = grátis)" value={fd.price} onChange={e => setFul("delivery", { ...fd, price: e.target.value })} />
              <input className={inputCls} placeholder="Área (ex: Londres)" value={fd.area || ""} onChange={e => setFul("delivery", { ...fd, area: e.target.value })} />
              <input className={`${inputCls} col-span-2`} placeholder="Condições (ex: 2-3 dias úteis)" value={fd.conditions || ""} onChange={e => setFul("delivery", { ...fd, conditions: e.target.value })} />
            </div>
          )}
          {f.fulfilment.modes.includes("pickup") && (
            <div className="grid grid-cols-2 gap-2 p-3 rounded-xl bg-zinc-50 border border-zinc-200">
              <input data-testid="prod-pickup-location" className={inputCls} placeholder="Local (zona, nunca morada exacta)" value={fp.location || ""} onChange={e => setFul("pickup", { ...fp, location: e.target.value })} />
              <input className={inputCls} placeholder="Horário" value={fp.hours || ""} onChange={e => setFul("pickup", { ...fp, hours: e.target.value })} />
              <input className={`${inputCls} col-span-2`} placeholder="Instruções" value={fp.instructions || ""} onChange={e => setFul("pickup", { ...fp, instructions: e.target.value })} />
            </div>
          )}
        </L>

        <div className="grid grid-cols-2 gap-2 pt-2">
          <button data-testid="prod-save-draft" disabled={busy} className="py-3 rounded-xl border border-zinc-300 font-semibold text-sm hover:border-black disabled:opacity-50">Guardar rascunho</button>
          <button data-testid="prod-save-submit" type="button" disabled={busy} onClick={(e) => save(e, true)} className="py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] text-white font-semibold text-sm disabled:opacity-50">Guardar e submeter</button>
        </div>
      </form>
    </div>
  );
}
